import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { AssignPlotOwnerModal } from '@/components/plots/assign-plot-owner-modal'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { CreateDocumentRequestModal } from '@/components/document-requests/create-document-request-modal'
import { DocumentRequestCard } from '@/components/document-requests/document-request-card'
import {
  useAssignPlotOwner,
  usePlot,
  usePlotHistory,
  useReleasePlotOwner,
  useUpdatePlot,
} from '@/hooks/use-plots'
import { useDocumentRequests } from '@/hooks/use-document-requests'
import { getApiErrorMessage } from '@/lib/api'
import { formatKES } from '@/lib/auth'

export interface PlotDetailPageProps {
  plotsBasePath: string
  membersBasePath: string
  canManage: boolean
}

export function PlotDetailPage({ plotsBasePath, membersBasePath, canManage }: PlotDetailPageProps) {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading, error } = usePlot(id)
  const { data: historyData } = usePlotHistory(id)
  const updatePlot = useUpdatePlot(id ?? '')
  const assignOwner = useAssignPlotOwner(id ?? '')
  const releaseOwner = useReleasePlotOwner(id ?? '')

  const plot = data?.data
  const history = historyData?.data ?? []

  const { data: documentRequestsData } = useDocumentRequests({ plotId: id })
  const documentRequests = documentRequestsData?.data ?? []

  const [assignOpen, setAssignOpen] = useState(false)
  const [requestDocsOpen, setRequestDocsOpen] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [developmentStatus, setDevelopmentStatus] = useState<'developer' | 'owner'>('owner')
  const [section, setSection] = useState('')
  const [mainAvenue, setMainAvenue] = useState('')
  const [courtOrStreet, setCourtOrStreet] = useState('')
  const [notes, setNotes] = useState('')
  const [boreholeMeterNumber, setBoreholeMeterNumber] = useState('')
  const [kplcMeterNumber, setKplcMeterNumber] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)

  function startEdit() {
    if (!plot) return
    setDevelopmentStatus(plot.developmentStatus)
    setSection(plot.section ?? '')
    setMainAvenue(plot.mainAvenue ?? '')
    setCourtOrStreet(plot.courtOrStreet ?? '')
    setNotes(plot.notes ?? '')
    setBoreholeMeterNumber(plot.boreholeMeterNumber ?? '')
    setKplcMeterNumber(plot.kplcMeterNumber ?? '')
    setEditMode(true)
  }

  async function handleSaveEdit() {
    setActionError(null)
    try {
      await updatePlot.mutateAsync({
        developmentStatus,
        section: section || undefined,
        mainAvenue: mainAvenue || undefined,
        courtOrStreet: courtOrStreet || undefined,
        notes: notes || undefined,
        boreholeMeterNumber,
        kplcMeterNumber,
      })
      setEditMode(false)
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Could not save changes'))
    }
  }

  async function handleRelease(overrideDuesReason?: string) {
    if (!overrideDuesReason && !confirm('Release this plot from its current owner?')) return
    setActionError(null)
    try {
      const result = await releaseOwner.mutateAsync({ overrideDuesReason })
      if (result.data.pendingApproval) {
        alert('Release requested — sent for committee/admin approval.')
      }
    } catch (err) {
      const message = getApiErrorMessage(err, 'Could not release plot')
      if (!overrideDuesReason && message.includes('outstanding balance')) {
        const reason = prompt(
          `${message}\n\nEnter a reason to override and release anyway (leave blank to cancel):`,
        )
        if (reason?.trim()) {
          await handleRelease(reason.trim())
        }
        return
      }
      setActionError(message)
    }
  }

  if (isLoading) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-muted-foreground">Loading plot…</p>
      </PageShell>
    )
  }

  if (error || !plot) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-destructive">Plot not found.</p>
        <Link to={plotsBasePath} className="mt-4 inline-block text-sm text-primary">
          Back to plots
        </Link>
      </PageShell>
    )
  }

  const statusVariant =
    plot.status === 'occupied' ? 'paid' : plot.status === 'reserved' ? 'pending' : 'draft'

  return (
    <PageShell>
      <PageHeader
        label="Management"
        title={`Plot ${plot.newNumber}`}
        description={
          plot.plotType === 'special_use'
            ? 'Special-use area'
            : `${plot.courtOrStreet ?? ''} ${plot.section ? `· ${plot.section}` : ''}`.trim()
        }
        action={
          <Link
            to={plotsBasePath}
            className="inline-flex items-center gap-2 font-sans text-sm text-primary hover:text-green-lt"
          >
            <ArrowLeft className="size-4" aria-hidden />
            Back to plots
          </Link>
        }
      />

      {plot.pendingApproval && (
        <p className="mb-4 rounded border border-status-pending-bg bg-status-pending-bg px-4 py-2.5 font-sans text-sm text-status-pending-text">
          An ownership change is pending committee/admin approval — no further changes can be
          requested until it's decided.
        </p>
      )}

      {canManage && (
        <div className="mb-6 flex flex-wrap gap-2">
          {!plot.pendingApproval && (
            <Button type="button" onClick={() => setAssignOpen(true)}>
              {plot.currentOwner ? 'Transfer ownership' : 'Assign owner'}
            </Button>
          )}
          {plot.currentOwner && !plot.pendingApproval && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => void handleRelease()}
              loading={releaseOwner.isPending}
            >
              Release plot
            </Button>
          )}
          {plot.currentOwner && (
            <Button type="button" variant="secondary" onClick={() => setRequestDocsOpen(true)}>
              Request documents
            </Button>
          )}
          {!editMode ? (
            <Button type="button" variant="secondary" onClick={startEdit}>
              Edit details
            </Button>
          ) : (
            <>
              <Button
                type="button"
                onClick={() => void handleSaveEdit()}
                loading={updatePlot.isPending}
              >
                Save
              </Button>
              <Button type="button" variant="secondary" onClick={() => setEditMode(false)}>
                Cancel
              </Button>
            </>
          )}
        </div>
      )}

      {actionError && (
        <p className="mb-4 font-sans text-sm text-destructive" role="alert">
          {actionError}
        </p>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <CardTitle>Plot details</CardTitle>
          <dl className="mt-4 space-y-3 font-sans text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Plot number</dt>
              <dd className="font-medium text-ink">{plot.newNumber}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Legacy number</dt>
              <dd className="text-ink">{plot.oldNumber}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <Badge variant={statusVariant} className="capitalize">
                  {plot.status}
                </Badge>
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Type</dt>
              <dd className="capitalize text-ink">{plot.plotType.replace('_', ' ')}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Development status</dt>
              <dd>
                <Badge variant={plot.developmentStatus === 'developer' ? 'paid' : 'draft'}>
                  {plot.developmentStatus === 'developer' ? 'Developer' : 'Owner'}
                </Badge>
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Current owner</dt>
              <dd>
                {plot.currentOwner ? (
                  <Link
                    to={`${membersBasePath}/${plot.currentOwner.memberId}`}
                    className="text-primary hover:text-green-lt"
                  >
                    {plot.currentOwner.name}
                  </Link>
                ) : (
                  <span className="text-muted-foreground">None</span>
                )}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Outstanding balance</dt>
              <dd
                className={
                  plot.outstandingBalance > 0 ? 'font-medium text-destructive' : 'text-ink'
                }
              >
                {plot.outstandingBalance > 0
                  ? `${formatKES(plot.outstandingBalance)} due`
                  : formatKES(0)}
              </dd>
            </div>
          </dl>

          {canManage && editMode ? (
            <div className="mt-6 space-y-4">
              <div>
                <label className="mb-1.5 block font-sans text-sm font-medium text-ink">
                  Development status
                </label>
                <select
                  value={developmentStatus}
                  onChange={(e) => setDevelopmentStatus(e.target.value as 'developer' | 'owner')}
                  className="flex h-10 w-full rounded border border-input bg-card px-3 font-sans text-sm"
                >
                  <option value="owner">Owner — not yet built</option>
                  <option value="developer">Developer — building or moved in</option>
                </select>
              </div>
              <Input label="Section" value={section} onChange={(e) => setSection(e.target.value)} />
              <Input
                label="Main avenue"
                value={mainAvenue}
                onChange={(e) => setMainAvenue(e.target.value)}
              />
              <Input
                label="Court / street"
                value={courtOrStreet}
                onChange={(e) => setCourtOrStreet(e.target.value)}
              />
              <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
              <Input
                label="Borehole meter no."
                value={boreholeMeterNumber}
                onChange={(e) => setBoreholeMeterNumber(e.target.value)}
              />
              <Input
                label="KPLC meter no."
                value={kplcMeterNumber}
                onChange={(e) => setKplcMeterNumber(e.target.value)}
              />
            </div>
          ) : (
            <dl className="mt-6 space-y-3 border-t border-border-2 pt-4 font-sans text-sm">
              <div>
                <dt className="text-muted-foreground">Borehole meter no.</dt>
                <dd className="text-ink">{plot.boreholeMeterNumber ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">KPLC meter no.</dt>
                <dd className="text-ink">{plot.kplcMeterNumber ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Section</dt>
                <dd className="text-ink">{plot.section ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Main avenue</dt>
                <dd className="text-ink">{plot.mainAvenue ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Court / street</dt>
                <dd className="text-ink">{plot.courtOrStreet ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Notes</dt>
                <dd className="text-ink">{plot.notes ?? '—'}</dd>
              </div>
            </dl>
          )}
        </Card>

        <Card className="p-6">
          <CardTitle>Ownership history</CardTitle>
          {history.length === 0 ? (
            <p className="mt-4 font-sans text-sm text-muted-foreground">
              No ownership records yet.
            </p>
          ) : (
            <ul className="mt-4 space-y-4">
              {history.map((item) => (
                <li
                  key={item.id}
                  className="border-b border-border-2 pb-4 font-sans text-sm last:border-0"
                >
                  <Link
                    to={`${membersBasePath}/${item.memberId}`}
                    className="font-medium text-primary hover:text-green-lt"
                  >
                    {item.memberName}
                  </Link>
                  <p className="mt-1 text-xs text-muted-foreground">
                    From{' '}
                    {new Date(item.acquiredAt * 1000).toLocaleDateString('en-KE', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                    {item.releasedAt &&
                      ` · Until ${new Date(item.releasedAt * 1000).toLocaleDateString('en-KE', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}`}
                    {!item.releasedAt && ' · Current'}
                  </p>
                  {item.transferNote && (
                    <p className="mt-1 text-xs text-muted-foreground">{item.transferNote}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {documentRequests.length > 0 && (
        <div className="mt-6 space-y-4">
          <h2 className="font-serif text-lg text-ink">Requested documents</h2>
          {documentRequests.map((request) => (
            <DocumentRequestCard key={request.id} request={request} />
          ))}
        </div>
      )}

      {requestDocsOpen && id && (
        <CreateDocumentRequestModal plotId={id} onClose={() => setRequestDocsOpen(false)} />
      )}

      {canManage && (
        <AssignPlotOwnerModal
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          plotLabel={plot.newNumber}
          currentOwnerName={plot.currentOwner?.name}
          outstandingBalance={plot.outstandingBalance}
          loading={assignOwner.isPending}
          onAssign={async (memberId, transferNote, overrideDuesReason) => {
            const result = await assignOwner.mutateAsync({
              memberId,
              transferNote,
              overrideDuesReason,
            })
            if (result.data.pendingApproval) {
              alert('Transfer requested — sent for committee/admin approval.')
            }
          }}
        />
      )}
    </PageShell>
  )
}
